import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import QuillEditor from '../components/QuillEditor';

const emptyPage = {
  title: '',
  slug: '',
  content: '',
  is_active: true,
  meta_title: '',
  meta_description: '',
  og_image: '',
};

export default function AdminPages() {
  useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyPage });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await api.get('/pages/all');
      setPages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Sayfalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyPage });
    setImage(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (page) => {
    setEditing(page);
    setForm({
      title: page.title || '',
      slug: page.slug || '',
      content: page.content || '',
      is_active: page.is_active !== undefined ? page.is_active : true,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      og_image: page.og_image || '',
    });
    setImage(null);
    setImagePreview(page.image_path ? getImageUrl(page.image_path) : null);
    setShowModal(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (image) {
        formData.append('image', image);
      } else if (editing && editing.image_path) {
        formData.append('existing_image', editing.image_path);
      }

      if (editing) {
        await api.put(`/pages/${editing.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Sayfa güncellendi');
      } else {
        await api.post('/pages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Sayfa eklendi');
      }
      setShowModal(false);
      fetchPages();
    } catch (err) {
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sayfayi silmek istediginize emin misiniz?')) return;
    try {
      await api.delete(`/pages/${id}`);
      toast.success('Sayfa silindi');
      fetchPages();
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Sayfa Yönetimi</h2>
          <button className="btn btn-primary" onClick={openAdd}>
            <FaPlus /> Yeni Sayfa
          </button>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Görsel</th>
              <th>Başlık</th>
              <th>Slug</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  Henüz sayfa eklenmemiş
                </td>
              </tr>
            ) : (
              pages.map((page) => (
                <tr key={page.id}>
                  <td>
                    {page.image_path && (
                      <img src={getImageUrl(page.image_path)} alt={page.title} />
                    )}
                  </td>
                  <td>{page.title}</td>
                  <td><code>{page.slug}</code></td>
                  <td>
                    <span className={`badge ${page.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {page.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-primary btn-sm" onClick={() => openEdit(page)}>
                        <FaEdit />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(page.id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Sayfa Düzenle' : 'Yeni Sayfa'}</h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <label>Başlık</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>İçerik</label>
                <QuillEditor
                  value={form.content}
                  onChange={(val) => handleChange('content', val)}
                />
              </div>
              <div className="form-group">
                <label>Durum</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                  <span>{form.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Görsel</label>
                <input type="file" accept="image/*" onChange={handleImageChange} />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                )}
              </div>
              <div className="form-group">
                <label>Meta Başlık</label>
                <input
                  type="text"
                  value={form.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Meta Açıklama</label>
                <input
                  type="text"
                  value={form.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                />
              </div>
              <div className="form-group full-width">
                <label>OG Image URL</label>
                <input
                  type="text"
                  value={form.og_image}
                  onChange={(e) => handleChange('og_image', e.target.value)}
                  placeholder="Sosyal medya paylasim gorseli URL'si"
                />
                {form.og_image && (
                  <img src={form.og_image} alt="OG Preview" className="image-preview" style={{ maxHeight: '100px', marginTop: '8px' }} />
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
